import type { IHttpClient } from '@wix/yoshi-flow-editor';
import {
  queryMenuOrderingSettings,
  listMenusAvailabilityStatus,
} from '@wix/ambassador-restaurants-menu-settings-v1-menu-ordering-settings/http';
import type {
  TimeSlot,
  MenuOrderingSettings,
} from '@wix/ambassador-restaurants-menu-settings-v1-menu-ordering-settings/types';
import type { FedopsLogger } from '../utils/monitoring/FedopsLogger';
import type { ReportError } from './utils/getMonitoredApiCall';
import { getMonitoredApiCall } from './utils/getMonitoredApiCall';
import {
  listFirstAvailableTimeSlotsForMenus,
  calculateFirstAvailableTimeSlotsPerMenu,
} from '@wix/ambassador-restaurants-operations-v1-operation/http';
import type { Address } from '@wix/ambassador-restaurants-operations-v1-operation/types';
import type { ErrorHandlerPublicAPI } from '@wix/error-handler';
import type { PanoramaErrorMonitor } from '@wix/panorama-client/dist/types/panorama-error-monitor';
import type { ErrorMonitorUserData } from '@wix/panorama-client/dist/types/types';
import type { IBIReporterService } from 'root/services/biReporterService';
import { menusState } from 'root/states/MenusState';
import { itemState } from 'root/states/itemsState';
import {
  getMenusBIFields,
  getOperationDurationParamsByOperationId,
} from 'root/utils/BIDurationUtils';

type MenuOrderingSettingsListResponse = {
  menuOrderingSettings: Required<MenuOrderingSettings[]>;
  next: (() => Promise<MenuOrderingSettingsListResponse | undefined>) | undefined;
};

export class OrdersSettingsClient {
  private operationDurationBIEvent?: (params: unknown) => void;
  constructor(
    private httpClient: IHttpClient,
    private errorHandler: ErrorHandlerPublicAPI,
    private errorMonitor?: PanoramaErrorMonitor<ErrorMonitorUserData>,
    private fedopsLogger?: FedopsLogger,
    private reportError?: ReportError,
    private biReporterService?: IBIReporterService,
    private useCalculateFirstAvailableAPI?: boolean
  ) {
    this.operationDurationBIEvent = this.biReporterService
      ?.reportOperationDurationBiEvent as unknown as (params: unknown) => void;
  }

  private fetchMenuOrderingSettingsByOperationId = async (
    operationId: string | undefined,
    cursor: string | null = null,
    onError?: (error: Error) => void
  ): Promise<MenuOrderingSettingsListResponse | undefined> => {
    const extraFields = getMenusBIFields(menusState, itemState);
    const { withErrorHandler, getResolvedError } = this.errorHandler;
    try {
      const { data = {} } = await getMonitoredApiCall({
        operationDurationParams: {
          ...getOperationDurationParamsByOperationId(operationId),
          useSSRCache: false,
          extraFields,
        },
        operationDurationBIEvent: this.operationDurationBIEvent,
        name: 'fetchMenuOrderingSettingsByOperationId',
        callback: () =>
          withErrorHandler(
            () =>
              this.httpClient
                .request(
                  queryMenuOrderingSettings({
                    query: {
                      ...(cursor
                        ? { cursorPaging: { cursor } }
                        : { filter: { operationId, onlineOrderingEnabled: true } }),
                    },
                  })
                )
                .then((response) => response.data),
            {
              errorCodesMap: {},
            }
          ),
        fedops: {
          start: this.fedopsLogger?.fetchMenuOrdersSettingsStarted,
          end: this.fedopsLogger?.fetchMenuOrdersSettingsEnded,
        },
        errorMonitor: this.errorMonitor,
        reportError: this.reportError,
        onError: (e) => {
          const error = getResolvedError(e);
          console.error(error);
          return onError?.(e);
        },
      });

      const { hasNext, cursors } = data.pagingMetadata ?? {};
      return {
        menuOrderingSettings: (data.menuOrderingSettings || []) as MenuOrderingSettings[],
        next: hasNext
          ? this.fetchMenuOrderingSettingsByOperationId.bind(
              this,
              operationId,
              cursors?.next,
              onError
            )
          : undefined,
      };
    } catch (e: unknown) {
      const error = getResolvedError(e);
      console.error(error);
      onError?.(e as Error);
      return undefined;
    }
  };

  getAllMenuOrderingSettingsByOperationId = async (
    operationId: string | undefined,
    onError?: (error: Error) => void
  ) => {
    const menuOrderingSettings = [];
    let response = await this.fetchMenuOrderingSettingsByOperationId(operationId, null, onError);

    if (response) {
      menuOrderingSettings.push(...response.menuOrderingSettings);

      while (response?.next) {
        response = await response?.next();
        menuOrderingSettings.push(...(response?.menuOrderingSettings ?? []));
      }
    }
    return { menuOrderingSettings };
  };

  getMenuOrderingSettingsByOperationId = async (
    operationId: string | undefined,
    onError?: (error: Error) => void
  ) => {
    return this.getAllMenuOrderingSettingsByOperationId(operationId, onError);
  };

  listMenusAvailabilityStatusByTimeslot = async (
    operationId: string,
    timeSlot: TimeSlot,
    onError?: (error: Error) => void
  ) => {
    const extraFields = getMenusBIFields(menusState, itemState);
    const { withErrorHandler, getResolvedError } = this.errorHandler;
    const { data = {} } = await getMonitoredApiCall({
      operationDurationParams: {
        ...getOperationDurationParamsByOperationId(operationId),
        useSSRCache: false,
        extraFields,
      },
      operationDurationBIEvent: this.operationDurationBIEvent,
      name: 'listMenusAvailabilityStatusByTimeslot',
      callback: () =>
        withErrorHandler(
          () =>
            this.httpClient
              .request(listMenusAvailabilityStatus({ operationId, timeSlot }))
              .then((response) => response.data),
          {
            errorCodesMap: {},
          }
        ),
      fedops: {
        start: this.fedopsLogger?.listMenusAvailabilityStatusByTimeslotStarted,
        end: this.fedopsLogger?.listMenusAvailabilityStatusByTimeslotEnded,
      },
      errorMonitor: this.errorMonitor,
      reportError: this.reportError,
      onError: (e) => {
        const error = getResolvedError(e);
        console.error(error);
        return onError?.(e);
      },
    });

    return data;
  };

  listFirstAvailableTimeSlotsForMenus = async ({
    operationId = '',
    deliveryAddress,
    onError,
  }: {
    operationId?: string;
    deliveryAddress?: Address;
    onError?: (error: Error) => void;
  }) => {
    return this.useCalculateFirstAvailableAPI
      ? this._calculateFirstAvailableTimeSlotsForMenus({ operationId, deliveryAddress, onError })
      : this._listFirstAvailableTimeSlotsForMenus({ operationId, deliveryAddress, onError });
  };

  private async _listFirstAvailableTimeSlotsForMenus({
    deliveryAddress,
    operationId = '',
    onError,
  }: {
    operationId?: string;
    deliveryAddress?: Address;
    onError?: (error: Error) => void;
  }) {
    const extraFields = getMenusBIFields(menusState, itemState);

    const { withErrorHandler, getResolvedError } = this.errorHandler;
    const { data = {} } = await getMonitoredApiCall({
      operationDurationParams: {
        ...getOperationDurationParamsByOperationId(operationId),
        useSSRCache: false,
        extraFields,
      },
      operationDurationBIEvent: this.operationDurationBIEvent,
      name: 'listFirstAvailableTimeSlotsForMenus',
      callback: () =>
        withErrorHandler(
          () =>
            this.httpClient
              .request(
                listFirstAvailableTimeSlotsForMenus({
                  operationId,
                  deliveryAddress,
                })
              )
              .then((response) => response.data),
          {
            errorCodesMap: {},
          }
        ),
      fedops: {
        start: this.fedopsLogger?.listFirstAvailableTimeSlotsForMenusStarted,
        end: this.fedopsLogger?.listFirstAvailableTimeSlotsForMenusEnded,
      },
      errorMonitor: this.errorMonitor,
      reportError: this.reportError,
      onError: (e) => {
        const error = getResolvedError(e);
        console.error(error);
        return onError?.(e);
      },
    });

    return data;
  }

  private async _calculateFirstAvailableTimeSlotsForMenus({
    deliveryAddress,
    operationId = '',
    onError,
  }: {
    operationId?: string;
    deliveryAddress?: Address;
    onError?: (error: Error) => void;
  }) {
    const extraFields = getMenusBIFields(menusState, itemState);

    const { withErrorHandler, getResolvedError } = this.errorHandler;
    const { data = {} } = await getMonitoredApiCall({
      operationDurationParams: {
        ...getOperationDurationParamsByOperationId(operationId),
        useSSRCache: false,
        extraFields,
      },
      operationDurationBIEvent: this.operationDurationBIEvent,
      name: 'calculateFirstAvailableTimeSlotsForMenus',
      callback: () =>
        withErrorHandler(
          () =>
            this.httpClient
              .request(
                calculateFirstAvailableTimeSlotsPerMenu({
                  operationId,
                  deliveryAddress,
                })
              )
              .then((response) => response.data),
          {
            errorCodesMap: {},
          }
        ),
      fedops: {
        start: this.fedopsLogger?.calculateFirstAvailableTimeSlotsForMenusStarted,
        end: this.fedopsLogger?.calculateFirstAvailableTimeSlotsForMenusEnded,
      },
      errorMonitor: this.errorMonitor,
      reportError: this.reportError,
      onError: (e) => {
        const error = getResolvedError(e);
        console.error(error);
        return onError?.(e);
      },
    });

    return data;
  }
}
